import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CreateUserRequest } from 'app/shared/types/user/create-user-request';
import { PatchOperation } from 'app/shared/types/user/patch-operation';
import { PatchUserRequest } from 'app/shared/types/user/patch-user-request';
import { UserInformation } from 'app/shared/types/user/user-information';
import { UserResponse } from 'app/shared/types/user/user-response';
import { UserListResponse } from 'app/shared/types/user/user-list-response';
import { catchError, EMPTY, Observable, shareReplay, tap } from 'rxjs';
import { ApiResponse } from 'app/shared/types';
import { BaseApiService } from 'app/shared/services';
import { environment } from 'environment/environment';
import { CONTROLLERS } from 'app/shared/services/controllers';
import { ContextService } from 'app/core/services/context.service';

@Injectable({
  providedIn: 'root',
})
export class UsersService extends BaseApiService<
  ApiResponse<UserResponse[]>,
  ApiResponse<UserResponse>,
  ApiResponse<{ id: string }>
> {
  public contextService = inject(ContextService);
  private loggedInUserWithRoles$: Observable<ApiResponse<UserInformation>> | null = null;

  constructor(public override http: HttpClient) {
    super(http, environment.authApiBaseUrl);
  }

  public override controllerName(): string {
    return CONTROLLERS.users;
  }

  public createUser(createUserRequest: CreateUserRequest): Observable<ApiResponse<UserResponse>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<ApiResponse<UserResponse>>(`${this.api}/${this.controllerName()}`, createUserRequest, {
      headers,
    });
  }

  public getAllUsers(): Observable<ApiResponse<UserResponse[]>> {
    const headers = new HttpHeaders({ Accept: 'application/json' });
    return this.http
      .get<ApiResponse<UserResponse[]>>(`${this.api}/${this.controllerName()}`, { headers })
      .pipe(shareReplay(1));
  }

  public getAllUsersForDownload(): Observable<ApiResponse<UserListResponse[]>> {
    const headers = new HttpHeaders({
      Accept: 'application/vnd.users.list.download+json',
    });
    return this.http.get<ApiResponse<UserListResponse[]>>(`${this.api}/${this.controllerName()}`, { headers });
  }

  public toggleActivateUser(userId: string, isActive: boolean): Observable<ApiResponse<UserResponse>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json-patch+json,application/vnd.patch.user.toggle.activate+json',
    });
    const patchDocument = [{ op: 'replace', path: '/isActive', value: isActive }];
    return this.http.patch<ApiResponse<UserResponse>>(`${this.api}/${this.controllerName()}/${userId}`, patchDocument, {
      headers,
    });
  }

  public patchUser(userId: string, patchDocument: PatchOperation[]): Observable<ApiResponse<PatchUserRequest>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json-patch+json',
    });
    return this.http.patch<ApiResponse<PatchUserRequest>>(
      `${this.api}/${this.controllerName()}/${userId}`,
      patchDocument,
      { headers }
    );
  }

  public getUser(userId: string): Observable<ApiResponse<UserInformation>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.get<ApiResponse<UserInformation>>(`${this.api}/${this.controllerName()}/${userId}`, { headers });
  }

  public getLoggedInUserWithRoles(): Observable<ApiResponse<UserInformation>> {
    if (!this.loggedInUserWithRoles$) {
      this.loggedInUserWithRoles$ = this.http
        .get<ApiResponse<UserInformation>>(`${this.api}/${this.controllerName()}/me`)
        .pipe(
          tap(({ value }) => {
            this.contextService.userInfo.next(value);
          }),
          shareReplay(1),
          catchError(() => {
            this.loggedInUserWithRoles$ = null;
            return EMPTY;
          })
        );
    }
    return this.loggedInUserWithRoles$;
  }
}
